import React from "react";
import styled from "styled-components/macro";
import { Typo16Regular, Typo32Medium } from "../UIComponents/Typography";
import GrayTextBox from "../../components/GrayTextBox";

import { t } from "../../utils/intl";
import infoLogo from "../../assets/i-circle-icn.svg";

interface RestTable {
  [key: string]: number;
}

interface Iprops {
  calculationResult: RestTable[];
}
export const BasicTable = ({ calculationResult }: Iprops) => {
  const formatNumber = (value: number) => {
    if (value === 0) {
      return value;
    }
    return new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2 }).format(Number(value));
  };

  return (
    <>
      <GrayTextBox>
        <ContentColumnWrapper>
          <Typo32Medium>{t("tableContentGrayTextBox.howReadTable")}</Typo32Medium>
          <Typo16Regular>
            {t("tableContentGrayTextBox.firstText")}{" "}
            <Typo16Regular>{t("tableContentGrayTextBox.firstTextBold")}</Typo16Regular>
          </Typo16Regular>
          <Typo16Regular>
            {t("tableContentGrayTextBox.secondTextFirst")}{" "}
            <Typo16Regular>{t("tableContentGrayTextBox.secondTextSecond")}</Typo16Regular>{" "}
            {t("tableContentGrayTextBox.secondTextThird")}
            <Typo16Regular>{t("tableContentGrayTextBox.secondTextFour")}</Typo16Regular>
            {t("tableContentGrayTextBox.secondTextFifth")}{" "}
            <Typo16Regular>{t("tableContentGrayTextBox.secondTextSixth")}</Typo16Regular>
            {t("tableContentGrayTextBox.secondTextSeventh")}
          </Typo16Regular>
          <Typo16Regular>{t("tableContentGrayTextBox.thirdText")}</Typo16Regular>
        </ContentColumnWrapper>
      </GrayTextBox>

      <GrayTextBox>
        <ContentRowWrapper>
          <Icon src={infoLogo} />
          <Typo16Regular>{t("tableContentSmallGrayTextBox.Text")}</Typo16Regular>
        </ContentRowWrapper>
      </GrayTextBox>

      <TextContainer>
        <MainInfo>{t("tableContentWhiteText.firstText")}</MainInfo>
        <DefaultInfo>{t("tableContentWhiteText.secondText")}</DefaultInfo>
        <DefaultInfo>{t("tableContentWhiteText.thirdText")}</DefaultInfo>
        <DefaultInfo>{t("tableContentWhiteText.fourthText")}</DefaultInfo>
      </TextContainer>
      <TableContainer>
        <Wrapper>
          <TableHeaderRow>
            <TableHeaderCell style={{ maxWidth: "118px" }}>
              {t("form.YearInsurance")}
            </TableHeaderCell>
            <TableHeaderCell>{t("form.guaranteedRedemption")}</TableHeaderCell>
            <TableHeaderCell>{t("form.guaranteedDeposit")}</TableHeaderCell>
            <TableHeaderCell>{t("form.declaredAnnual")}</TableHeaderCell>
          </TableHeaderRow>

          {calculationResult.map(el => {
            return (
              <TableHeaderRow key={el.year}>
                <TableCell style={{ maxWidth: "118px" }}>{el.year}</TableCell>
                <TableCell>{formatNumber(el.value1)}</TableCell>
                <TableCell>{formatNumber(el.value2)}</TableCell>
                <TableCell>{el.value3 + ` %`}</TableCell>
              </TableHeaderRow>
            );
          })}
        </Wrapper>
      </TableContainer>
    </>
  );
};

const DefaultInfo = styled.p`
  font-style: normal;
  margin: 2px 0 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
`;

const MainInfo = styled.p`
  font-style: normal;
  margin-bottom: 24px;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 64px;
`;

const TextContainer = styled.div`
  margin: 20px 0;
  font-size: 18px;
  line-height: 25px;
  page-break-inside: avoid;
`;

const Wrapper = styled.div`
  min-width: 770px;
  page-break-inside: avoid;
  border: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};
  @media print {
    border: none;
    border-top: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};
  }
`;

const TableHeaderRow = styled.div`
  display: flex;
  width: 100%;
  page-break-inside: avoid;

  @media print {
    border-top: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};
    border-bottom: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};
    border-left: ${({ theme }) => `solid 2px ${theme.colors.grayMedium} `};
    border-right: ${({ theme }) => `solid 2px ${theme.colors.grayMedium} `};
  }
`;

const TableHeaderCell = styled.div`
  text-align: center;
  width: 32%;
  height: 95px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;

  &:first-child {
    text-align: left;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }
  &:last-child {
    text-align: right;
    justify-content: end;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }

  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors.lighterGray};
  justify-content: center;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  &:not(:last-child) {
    border-right: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};

    @media print {
      border-right: ${({ theme }) => `solid 2px ${theme.colors.grayMedium} `};
    }
  }
`;

const TableCell = styled.div`
  text-align: center;
  width: 32%;
  padding: 18px 0;
  justify-content: center;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  align-items: center;
  border-top: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};

  display: table;
  page-break-inside: auto;

  @media print {
    border-top: none;
  }

  &:first-child {
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    text-align: left;
    padding-left: 12px;
  }

  &:last-child {
    text-align: right;
    padding-right: 12px;
  }

  &:not(:last-child) {
    border-right: ${({ theme }) => `solid 1px ${theme.colors.grayMedium} `};
    @media print {
      border-right: ${({ theme }) => `solid 2px ${theme.colors.grayMedium} `};
    }
  }
`;

const ContentRowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const ContentColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  span {
    margin-bottom: 24px;
  }

  span:last-child {
    margin-bottom: 0;
  }
`;

const Icon = styled.img`
  margin-right: 17px;
  filter: invert(58%) sepia(45%) saturate(10%) hue-rotate(68deg) brightness(95%) contrast(100%);
`;
